import { formulaText } from "./formula.js";
import { INDICATOR_OUTPUTS } from "./indicator.js";
/**
 * What a builder needs to offer each indicator kind: a label, and its
 * parameters with their defaults and bounds.
 *
 * Lives with the format rather than with the engine, because it describes what
 * a `.strat` file may legally contain. The engine reads `params`; this says
 * which params exist and what a sensible one looks like.
 *
 * `outputs` is derived from `INDICATOR_OUTPUTS` rather than repeated here. The
 * prototype listed them twice, in two files, with nothing to notice when they
 * drifted.
 */
const period = (d, max = 500) => ({
    key: "period",
    label: "Period",
    default: d,
    min: 1,
    max,
});
const ENTRIES = [
    // --- Moving averages -------------------------------------------------------
    {
        kind: "sma",
        label: "Simple Moving Average",
        short: "SMA",
        group: "Moving averages",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "ema",
        label: "Exponential Moving Average",
        short: "EMA",
        group: "Moving averages",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "wma",
        label: "Weighted Moving Average",
        short: "WMA",
        group: "Moving averages",
        description: "Linearly weighted: the newest bar counts most.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "smma",
        label: "Smoothed Moving Average (RMA)",
        short: "SMMA",
        group: "Moving averages",
        description: "Wilder's smoothing, the average inside RSI and ATR.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "hma",
        label: "Hull Moving Average",
        short: "HMA",
        group: "Moving averages",
        description: "Fast and smooth: a WMA of the difference of two WMAs.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "dema",
        label: "Double EMA",
        short: "DEMA",
        group: "Moving averages",
        description: "2×EMA − EMA(EMA): less lag than an EMA.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "tema",
        label: "Triple EMA",
        short: "TEMA",
        group: "Moving averages",
        description: "3×EMA − 3×EMA² + EMA³: less lag again.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "alma",
        label: "Arnaud Legoux Moving Average",
        short: "ALMA",
        group: "Moving averages",
        description: "A Gaussian-weighted window, shifted towards recent bars.",
        paneKind: "overlay",
        params: [
            period(9),
            { key: "offset", label: "Offset", default: 0.85, min: 0.01, max: 1, step: 0.01 },
            { key: "sigma", label: "Sigma", default: 6, min: 0.5, max: 50, step: 0.5 },
        ],
    },
    {
        kind: "lsma",
        label: "Least Squares Moving Average",
        short: "LSMA",
        group: "Moving averages",
        description: "The end point of a linear regression over the window.",
        paneKind: "overlay",
        params: [period(25)],
    },
    {
        kind: "mcginley",
        label: "McGinley Dynamic",
        short: "MGD",
        group: "Moving averages",
        description: "An average that speeds up in fast markets and slows in quiet ones.",
        paneKind: "overlay",
        params: [period(14)],
    },
    {
        kind: "kama",
        label: "Kaufman Adaptive Moving Average",
        short: "KAMA",
        group: "Moving averages",
        description: "Follows closely when price trends, flattens when it chops.",
        paneKind: "overlay",
        params: [period(10, 200)],
    },
    // --- Trend & channels -------------------------------------------------------
    {
        kind: "bbands",
        label: "Bollinger Bands",
        short: "BB",
        group: "Trend & channels",
        paneKind: "overlay",
        band: { upper: "upper", lower: "lower" },
        params: [period(20, 200), { key: "stdDev", label: "Std Dev", default: 2, min: 0.5, max: 5, step: 0.1 }],
    },
    {
        kind: "keltner",
        label: "Keltner Channels",
        short: "KC",
        group: "Trend & channels",
        description: "An EMA with bands a multiple of the ATR away.",
        paneKind: "overlay",
        band: { upper: "upper", lower: "lower" },
        params: [
            period(20, 200),
            { key: "multiplier", label: "Multiplier", default: 2, min: 0.1, max: 10, step: 0.1 },
            { key: "atrPeriod", label: "ATR Period", default: 10, min: 1, max: 200 },
        ],
    },
    {
        kind: "donchian",
        label: "Donchian Channels",
        short: "DC",
        group: "Trend & channels",
        description: "The highest high and lowest low of the window: breakouts, 52-week highs.",
        paneKind: "overlay",
        band: { upper: "upper", lower: "lower" },
        params: [period(20)],
    },
    {
        kind: "envelope",
        label: "Envelopes",
        short: "ENV",
        group: "Trend & channels",
        description: "An SMA with bands a fixed percentage above and below.",
        paneKind: "overlay",
        band: { upper: "upper", lower: "lower" },
        params: [period(20), { key: "percent", label: "Percent", default: 10, min: 0.1, max: 50, step: 0.1 }],
    },
    {
        kind: "supertrend",
        label: "SuperTrend",
        short: "ST",
        group: "Trend & channels",
        description: "An ATR trailing line that flips sides with the trend. Direction: +1 up, −1 down.",
        paneKind: "overlay",
        // Not drawn: its stepped line, jumping sides at every flip, read as noise
        // over the candles. Conditions still read every output.
        render: { value: "hidden", direction: "hidden", long: "hidden", short: "hidden" },
        tone: { long: "up", short: "down" },
        params: [
            { key: "period", label: "ATR Period", default: 10, min: 1, max: 200 },
            { key: "factor", label: "Factor", default: 3, min: 0.1, max: 20, step: 0.1 },
        ],
    },
    {
        kind: "psar",
        label: "Parabolic SAR",
        short: "SAR",
        group: "Trend & channels",
        description: "Stop-and-reverse dots that trail price and accelerate with the trend.",
        paneKind: "overlay",
        render: { value: "dots" },
        params: [
            { key: "start", label: "Start", default: 0.02, min: 0.001, max: 1, step: 0.001 },
            { key: "increment", label: "Increment", default: 0.02, min: 0.001, max: 1, step: 0.001 },
            { key: "maximum", label: "Maximum", default: 0.2, min: 0.01, max: 1, step: 0.01 },
        ],
    },
    {
        kind: "ichimoku",
        label: "Ichimoku Cloud",
        short: "Ichimoku",
        group: "Trend & channels",
        description: "Conversion, base and the cloud as it stands at each bar (no lagging span: it reads the future).",
        paneKind: "overlay",
        band: { upper: "spanA", lower: "spanB", bySign: true },
        params: [
            { key: "conversionPeriod", label: "Conversion", default: 9, min: 1, max: 200 },
            { key: "basePeriod", label: "Base", default: 26, min: 1, max: 200 },
            { key: "spanBPeriod", label: "Span B", default: 52, min: 1, max: 400 },
            { key: "displacement", label: "Displacement", default: 26, min: 1, max: 200 },
        ],
    },
    {
        kind: "adx",
        label: "Average Directional Index",
        short: "ADX",
        group: "Trend & channels",
        description: "Trend strength, with +DI and −DI for its direction.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [25] },
        params: [period(14, 200)],
    },
    // --- Oscillators --------------------------------------------------------
    {
        kind: "rsi",
        label: "Relative Strength Index",
        short: "RSI",
        group: "Oscillators",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [30, 70] },
        params: [period(14, 200)],
    },
    {
        kind: "macd",
        label: "MACD",
        short: "MACD",
        group: "Oscillators",
        paneKind: "subplot",
        scale: { guides: [0] },
        render: { histogram: "histogram" },
        params: [
            { key: "fastPeriod", label: "Fast Period", default: 12, min: 1, max: 100 },
            { key: "slowPeriod", label: "Slow Period", default: 26, min: 1, max: 200 },
            { key: "signalPeriod", label: "Signal Period", default: 9, min: 1, max: 100 },
        ],
    },
    {
        kind: "stoch",
        label: "Stochastic Oscillator",
        short: "Stoch",
        group: "Oscillators",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [20, 80] },
        params: [
            { key: "period", label: "%K Period", default: 14, min: 1, max: 100 },
            { key: "signalPeriod", label: "%D Period", default: 3, min: 1, max: 50 },
        ],
    },
    {
        kind: "cci",
        label: "Commodity Channel Index",
        short: "CCI",
        group: "Oscillators",
        description: "Distance of the typical price from its average, in mean deviations.",
        paneKind: "subplot",
        scale: { guides: [-100, 100] },
        params: [period(20, 200)],
    },
    {
        kind: "willr",
        label: "Williams %R",
        short: "%R",
        group: "Oscillators",
        description: "Where the close sits in the window's range, 0 at the top, −100 at the bottom.",
        paneKind: "subplot",
        scale: { min: -100, max: 0, guides: [-20, -80] },
        params: [period(14, 200)],
    },
    {
        kind: "stochrsi",
        label: "Stochastic RSI",
        short: "StochRSI",
        group: "Oscillators",
        description: "The stochastic of the RSI itself: faster, and pinned to its extremes more often.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [20, 80] },
        params: [
            { key: "rsiPeriod", label: "RSI Length", default: 14, min: 1, max: 200 },
            { key: "stochPeriod", label: "Stoch Length", default: 14, min: 1, max: 200 },
            { key: "smoothK", label: "K", default: 3, min: 1, max: 50 },
            { key: "smoothD", label: "D", default: 3, min: 1, max: 50 },
        ],
    },
    {
        kind: "roc",
        label: "Rate of Change",
        short: "ROC",
        group: "Oscillators",
        description: "Percent change over the period.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(9)],
    },
    {
        kind: "mom",
        label: "Momentum",
        short: "MOM",
        group: "Oscillators",
        description: "Price change over the period, in price units.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(10)],
    },
    {
        kind: "trix",
        label: "TRIX",
        short: "TRIX",
        group: "Oscillators",
        description: "Rate of change of a triple-smoothed EMA (TradingView's: of log price, ×10,000).",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(18, 200)],
    },
    {
        kind: "ao",
        label: "Awesome Oscillator",
        short: "AO",
        group: "Oscillators",
        description: "5- minus 34-bar SMA of the bar midpoint; bars green while rising.",
        paneKind: "subplot",
        scale: { guides: [0] },
        render: { value: "histogramSlope" },
        params: [
            { key: "fastPeriod", label: "Fast", default: 5, min: 1, max: 100 },
            { key: "slowPeriod", label: "Slow", default: 34, min: 1, max: 200 },
        ],
    },
    {
        kind: "ppo",
        label: "Percentage Price Oscillator",
        short: "PPO",
        group: "Oscillators",
        description: "MACD as a percentage of the slow EMA, so it compares across prices.",
        paneKind: "subplot",
        scale: { guides: [0] },
        render: { histogram: "histogram" },
        params: [
            { key: "fastPeriod", label: "Fast Period", default: 12, min: 1, max: 100 },
            { key: "slowPeriod", label: "Slow Period", default: 26, min: 1, max: 200 },
            { key: "signalPeriod", label: "Signal Period", default: 9, min: 1, max: 100 },
        ],
    },
    {
        kind: "cmo",
        label: "Chande Momentum Oscillator",
        short: "CMO",
        group: "Oscillators",
        description: "Up moves minus down moves over their total, −100 to 100.",
        paneKind: "subplot",
        scale: { min: -100, max: 100, guides: [-50, 50] },
        params: [period(9, 200)],
    },
    {
        kind: "tsi",
        label: "True Strength Index",
        short: "TSI",
        group: "Oscillators",
        description: "Double-smoothed momentum over double-smoothed absolute momentum.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [
            { key: "longPeriod", label: "Long", default: 25, min: 1, max: 200 },
            { key: "shortPeriod", label: "Short", default: 13, min: 1, max: 200 },
            { key: "signalPeriod", label: "Signal", default: 13, min: 1, max: 100 },
        ],
    },
    {
        kind: "uo",
        label: "Ultimate Oscillator",
        short: "UO",
        group: "Oscillators",
        description: "Buying pressure over three windows, weighted 4:2:1.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [30, 70] },
        params: [
            { key: "fastPeriod", label: "Fast", default: 7, min: 1, max: 100 },
            { key: "middlePeriod", label: "Middle", default: 14, min: 1, max: 200 },
            { key: "slowPeriod", label: "Slow", default: 28, min: 1, max: 400 },
        ],
    },
    {
        kind: "aroon",
        label: "Aroon",
        short: "Aroon",
        group: "Oscillators",
        description: "How recently the window's high and low were made; the oscillator is up minus down.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [30, 70] },
        render: { oscillator: "hidden" },
        tone: { up: "up", down: "down" },
        params: [period(14, 200)],
    },
    {
        kind: "vortex",
        label: "Vortex Indicator",
        short: "VI",
        group: "Oscillators",
        description: "Upward against downward movement; the lines crossing marks a turn.",
        paneKind: "subplot",
        scale: { guides: [1] },
        tone: { plus: "up", minus: "down" },
        params: [period(14, 200)],
    },
    {
        kind: "fisher",
        label: "Fisher Transform",
        short: "Fisher",
        group: "Oscillators",
        description: "Price turned into a near-normal distribution, so turns stand out as sharp peaks.",
        paneKind: "subplot",
        scale: { guides: [-1.5, 0, 1.5] },
        params: [period(9, 200)],
    },
    {
        kind: "coppock",
        label: "Coppock Curve",
        short: "Coppock",
        group: "Oscillators",
        description: "A WMA of two rates of change: a slow, long-term momentum gauge.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [
            { key: "period", label: "WMA Length", default: 10, min: 1, max: 200 },
            { key: "longRoc", label: "Long RoC", default: 14, min: 1, max: 200 },
            { key: "shortRoc", label: "Short RoC", default: 11, min: 1, max: 200 },
        ],
    },
    {
        kind: "dpo",
        label: "Detrended Price Oscillator",
        short: "DPO",
        group: "Oscillators",
        description: "Close against an SMA from half a window back: cycles without the trend.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(21, 200)],
    },
    {
        kind: "crsi",
        label: "Connors RSI",
        short: "CRSI",
        group: "Oscillators",
        description: "The average of a short RSI, the RSI of the up/down streak, and the rank of today's change.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [10, 90] },
        params: [
            { key: "rsiPeriod", label: "RSI Length", default: 3, min: 1, max: 100 },
            { key: "streakPeriod", label: "Up/Down Length", default: 2, min: 1, max: 100 },
            { key: "rankPeriod", label: "ROC Length", default: 100, min: 1, max: 500 },
        ],
    },
    // --- Volatility -----------------------------------------------------------
    {
        kind: "atr",
        label: "Average True Range",
        short: "ATR",
        group: "Volatility",
        paneKind: "subplot",
        scale: { min: 0 },
        params: [period(14, 200)],
    },
    {
        kind: "stdev",
        label: "Standard Deviation",
        short: "StdDev",
        group: "Volatility",
        description: "Population standard deviation of the close over the window.",
        paneKind: "subplot",
        scale: { min: 0 },
        params: [period(20)],
    },
    {
        kind: "bbpct",
        label: "Bollinger Bands %B",
        short: "%B",
        group: "Volatility",
        description: "Where the close sits between the bands: 1 at the upper, 0 at the lower.",
        paneKind: "subplot",
        scale: { guides: [0, 1] },
        params: [period(20, 200), { key: "stdDev", label: "Std Dev", default: 2, min: 0.5, max: 5, step: 0.1 }],
    },
    {
        kind: "bbw",
        label: "Bollinger BandWidth",
        short: "BBW",
        group: "Volatility",
        description: "The bands' width as a percent of the middle band: low readings are a squeeze.",
        paneKind: "subplot",
        scale: { min: 0 },
        params: [period(20, 200), { key: "stdDev", label: "Std Dev", default: 2, min: 0.5, max: 5, step: 0.1 }],
    },
    {
        kind: "chop",
        label: "Choppiness Index",
        short: "CHOP",
        group: "Volatility",
        description: "High when price chops sideways, low when it trends.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [38.2, 61.8] },
        params: [period(14, 200)],
    },
    {
        kind: "hv",
        label: "Historical Volatility",
        short: "HV",
        group: "Volatility",
        description: "Annualised deviation of log returns, as TradingView scales it (per bar × √365).",
        paneKind: "subplot",
        scale: { min: 0 },
        params: [period(10, 200)],
    },
    {
        kind: "massi",
        label: "Mass Index",
        short: "MI",
        group: "Volatility",
        description: "Widening high–low ranges; a bulge above 27 then back below 26.5 warns of a reversal.",
        paneKind: "subplot",
        scale: { guides: [26.5, 27] },
        params: [period(10, 200)],
    },
    // --- Volume ---------------------------------------------------------------
    {
        kind: "volume",
        label: "Volume",
        short: "Vol",
        group: "Volume",
        description: "Traded volume with its moving average, for rules like “volume crosses above its average”.",
        paneKind: "subplot",
        scale: { min: 0 },
        render: { value: "histogramCandle" },
        params: [{ key: "period", label: "MA Length", default: 20, min: 1, max: 500 }],
    },
    {
        kind: "vwap",
        label: "VWAP",
        short: "VWAP",
        group: "Volume",
        description: "Volume-weighted average price of the UTC day so far, with standard-deviation bands.",
        paneKind: "overlay",
        band: { upper: "upper", lower: "lower" },
        resetsDaily: true,
        params: [{ key: "multiplier", label: "Band Multiplier", default: 1, min: 0.1, max: 5, step: 0.1 }],
    },
    {
        kind: "vwma",
        label: "Volume Weighted Moving Average",
        short: "VWMA",
        group: "Volume",
        description: "A moving average that weights each close by its volume.",
        paneKind: "overlay",
        params: [period(20)],
    },
    {
        kind: "obv",
        label: "On Balance Volume",
        short: "OBV",
        group: "Volume",
        description: "Running total of volume, added on up closes and taken off on down closes; with its MA.",
        cumulative: true,
        paneKind: "subplot",
        params: [{ key: "period", label: "MA Length", default: 20, min: 1, max: 500 }],
    },
    {
        kind: "ad",
        label: "Accumulation/Distribution",
        short: "A/D",
        group: "Volume",
        description: "Running total of volume weighted by where each close sits in its bar's range; with its MA.",
        cumulative: true,
        paneKind: "subplot",
        params: [{ key: "period", label: "MA Length", default: 20, min: 1, max: 500 }],
    },
    {
        kind: "adosc",
        label: "Chaikin Oscillator",
        short: "ChaikinOsc",
        group: "Volume",
        description: "Fast minus slow EMA of Accumulation/Distribution.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [
            { key: "fastPeriod", label: "Fast", default: 3, min: 1, max: 100 },
            { key: "slowPeriod", label: "Slow", default: 10, min: 1, max: 200 },
        ],
    },
    {
        kind: "cmf",
        label: "Chaikin Money Flow",
        short: "CMF",
        group: "Volume",
        description: "Buying against selling pressure over the window, −1 to 1.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(20)],
    },
    {
        kind: "mfi",
        label: "Money Flow Index",
        short: "MFI",
        group: "Volume",
        description: "A volume-weighted RSI of the typical price.",
        paneKind: "subplot",
        scale: { min: 0, max: 100, guides: [20, 80] },
        params: [period(14, 200)],
    },
    {
        kind: "efi",
        label: "Elder's Force Index",
        short: "EFI",
        group: "Volume",
        description: "EMA of price change times volume: the force behind a move.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(13, 200)],
    },
    {
        kind: "eom",
        label: "Ease of Movement",
        short: "EOM",
        group: "Volume",
        description: "How far price moved per unit of volume, averaged.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [period(14, 200)],
    },
    {
        kind: "pvt",
        label: "Price Volume Trend",
        short: "PVT",
        group: "Volume",
        description: "Running total of volume times the percent change of the close; with its MA.",
        cumulative: true,
        paneKind: "subplot",
        params: [{ key: "period", label: "MA Length", default: 20, min: 1, max: 500 }],
    },
    {
        kind: "vo",
        label: "Volume Oscillator",
        short: "VO",
        group: "Volume",
        description: "Fast against slow EMA of volume, in percent: is volume expanding?",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [
            { key: "fastPeriod", label: "Short", default: 5, min: 1, max: 100 },
            { key: "slowPeriod", label: "Long", default: 10, min: 1, max: 200 },
        ],
    },
    {
        kind: "bop",
        label: "Balance of Power",
        short: "BOP",
        group: "Volume",
        description: "Close minus open over the bar's range: who won the bar, −1 to 1.",
        paneKind: "subplot",
        scale: { min: -1, max: 1, guides: [0] },
        params: [],
    },
    {
        kind: "rvi",
        label: "Relative Vigor Index",
        short: "RVGI",
        group: "Oscillators",
        description: "Close minus open against the range, smoothed: trends close strong.",
        paneKind: "subplot",
        scale: { guides: [0] },
        tone: { rvi: "up", signal: "down" },
        params: [period(10, 200)],
    },
    // --- Custom ------------------------------------------------------------------
    {
        kind: "formula",
        label: "Formula",
        short: "Formula",
        group: "Custom",
        description: "Arithmetic over indicators, prices and numbers, any bar back: A − B, A ÷ B, |A|. Elliott Wave Oscillator, a gap between averages.",
        paneKind: "subplot",
        scale: { guides: [0] },
        params: [],
    },
];
export function getCatalogEntry(kind) {
    const entry = INDICATOR_CATALOG.find((e) => e.kind === kind);
    if (!entry)
        throw new Error(`Unknown indicator kind: ${kind}`);
    return entry;
}
export const INDICATOR_CATALOG = ENTRIES.map((entry) => ({
    ...entry,
    outputs: [...INDICATOR_OUTPUTS[entry.kind]],
}));
export function catalogEntry(kind) {
    const entry = INDICATOR_CATALOG.find((e) => e.kind === kind);
    if (!entry)
        throw new Error(`No catalog entry for indicator kind "${kind}".`);
    return entry;
}
/**
 * An indicator's name from its kind and parameters: the catalog's short name
 * and every parameter in catalog order — "MACD(12,26,9)", "ST(10,3)", "A/D(20)".
 * Kinds this build doesn't know fall back to the kind itself, so a file from a
 * newer version still reads.
 */
export function indicatorAutoLabel(def, 
/**
 * The strategy's other indicators, so a formula can name what it reads —
 * "EMA(5) − EMA(35)" rather than "Formula". Without them it is "Formula".
 */
others) {
    const suffix = def.timeframe ? ` · ${def.timeframe}` : "";
    if (def.kind === "formula") {
        if (!def.formula || !others)
            return `Formula${suffix}`;
        return formulaText(def.formula, (leaf) => {
            if (leaf.kind === "const")
                return String(leaf.value);
            const ago = leaf.offset ? `[${leaf.offset}]` : "";
            if (leaf.kind === "price")
                return `${leaf.field}${ago}`;
            const ref = others.find((o) => o.id === leaf.ref);
            // One level only: a formula reading another formula names it by its
            // label, or by its id, rather than expanding it inline.
            const name = !ref
                ? leaf.ref
                : ref.label?.trim() || (ref.kind === "formula" ? ref.id : indicatorAutoLabel(ref));
            return `${name}${leaf.output ? ` ${leaf.output}` : ""}${ago}`;
        });
    }
    const entry = INDICATOR_CATALOG.find((e) => e.kind === def.kind);
    if (!entry)
        return def.kind.toUpperCase();
    const values = entry.params.map((spec) => def.params[spec.key] ?? spec.default);
    return (values.length ? `${entry.short}(${values.join(",")})` : entry.short) + suffix;
}
//# sourceMappingURL=catalog.js.map